import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateArticleSlug } from '../../i18n/utils/slugGenerator';
import LatestArticlesSkeleton from './LatestArticlesSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { IMAGES_BASE_URL } from '../../config/constants';
import './LatestArticles.scss';

const LatestArticles = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { 
        data: mainData, 
        loading: mainLoading, 
        error: mainError, 
        refetch: refetchMainData 
    } = useCachedFetch('/data/locales/mainData.json');
    
    const { 
        data: articlesData, 
        loading: articlesLoading, 
        error: articlesError, 
        refetch: refetchArticlesData 
    } = useCachedFetch('/data/locales/articles.json');

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [visibleArticles, setVisibleArticles] = useState(3);

    const handleRetry = useCallback(() => {
        refetchMainData();
        refetchArticlesData();
    }, [refetchMainData, refetchArticlesData]);

    useEffect(() => {
        if (mainLoading || articlesLoading) return;

        if (mainError || articlesError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const latestInsightsData = mainData?.TopMainData?.LatestInsights;
        const articlesArray = articlesData?.ArticleData || [];

        if (latestInsightsData && 
            latestInsightsData.contentAccent && 
            Array.isArray(articlesArray) && 
            articlesArray.length > 0) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [mainLoading, articlesLoading, mainError, articlesError, mainData, articlesData]);

    const getLocalizedTitle = useCallback((titleObj) => {
        return typeof titleObj === 'object'
            ? titleObj[currentLang] || titleObj.en
            : titleObj;
    }, [currentLang]);

    const mainContent = useMemo(() => 
        mainData?.TopMainData?.LatestInsights || null,
        [mainData]
    );

    const articles = useMemo(() => 
        articlesData?.ArticleData || [],
        [articlesData]
    );

    const sectionTitle = useMemo(() => 
        mainContent?.contentAccent?.[currentLang] || mainContent?.contentAccent?.en || '',
        [mainContent, currentLang]
    );

    const updateArticlesCount = useCallback(() => {
        if (window.innerWidth >= 1920) {
            setVisibleArticles(4);
        } else if (window.innerWidth >= 1200) {
            setVisibleArticles(3);
        }
    }, []);

    useEffect(() => {
        updateArticlesCount();
        window.addEventListener('resize', updateArticlesCount);
        return () => window.removeEventListener('resize', updateArticlesCount);
    }, [updateArticlesCount]);

    const latestArticles = useMemo(() => {
        if (!articles.length) return [];
        return articles.slice(-visibleArticles).reverse();
    }, [articles, visibleArticles]);

    const getArticleCategory = useCallback((article) => {
        const categoryName = article.webLink?.category?.categoryName;
        if (typeof categoryName === 'object') {
            return categoryName[currentLang] || categoryName.en || '';
        }
        return categoryName || '';
    }, [currentLang]);

    const getArticleDate = useCallback((article) => {
        const dateArticle = article.dateArticle;
        if (typeof dateArticle === 'object') {
            return dateArticle[currentLang] || dateArticle.en || '';
        }
        return dateArticle || '';
    }, [currentLang]);

    const getArticleLink = useCallback((article) => {
        const categorySlug = article.webLink?.category?.slug || '';
        const articleSlug = generateArticleSlug(article.mainArticleTitle);
        return `/blog/${categorySlug}/${articleSlug}`;
    }, []);

    const hasValidData = useMemo(() => 
        !hasError && mainContent && articles.length > 0 && latestArticles.length > 0,
        [hasError, mainContent, articles.length, latestArticles.length]
    );

    if (isLoading) {
        return <LatestArticlesSkeleton />;
    }

    if (!hasValidData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="latest__articles">
            <h2 className="latest__articles__title">
                {sectionTitle}
            </h2>
            <div className={`articles articles-${visibleArticles}`}>
                {latestArticles.map((article) => {
                    const articleTitle = getLocalizedTitle(article.mainArticleTitle);
                    const articleLink = getArticleLink(article);
                    const articleCategory = getArticleCategory(article);
                    const articleDate = getArticleDate(article);

                    return (
                        <div className="article__card" key={article.id}>
                            <Link
                                to={articleLink}
                                className="article__link"
                            >
                                <div className="article__image">
                                    <img
                                        className="article__image__pic"
                                        src={`${IMAGES_BASE_URL}${article.articleImage}`}
                                        onError={(e) => console.log('Failed to load article image:', e.target.src)}
                                        alt={articleTitle}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="article__info">
                                    <div className='article__info__date__wrapper'>
                                        <p className="article__category">
                                            {articleCategory}
                                        </p>
                                        <p className="article__date">
                                            {articleDate}
                                        </p>
                                    </div>
                                    <h3 className="article__title">
                                        {articleTitle}
                                    </h3>
                                </div>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(LatestArticles);